import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  SuperAdminPaymentEntry,
  SuperAdminPaymentStatut,
  SuperAdminTenantDetail,
} from "@victor/types";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { Modal } from "../../../ui/Modal";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import {
  Badge,
  EmptyInline,
  Field,
  Panel,
  SectionTitle,
  fmtDateTime,
  fmtMoney,
} from "./_ui";

type Props = { tenant: SuperAdminTenantDetail };

const STATUT_TONE: Record<
  SuperAdminPaymentStatut,
  Parameters<typeof Badge>[0]["tone"]
> = {
  paid: "emerald",
  succeeded: "emerald",
  open: "amber",
  draft: "slate",
  void: "slate",
  uncollectible: "red",
  failed: "red",
};

export function OverviewTab({ tenant }: Props) {
  const queryClient = useQueryClient();
  const [blockOpen, setBlockOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const [motif, setMotif] = useState("");

  const paymentsQ = useQuery({
    queryKey: ["tenant-payments", tenant.id],
    queryFn: () => api.payments.list(tenant.id),
  });

  const blockMut = useMutation({
    mutationFn: () => api.tenants.block(tenant.id, motif),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenant-detail", tenant.id] });
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setBlockOpen(false);
      setMotif("");
    },
  });

  const unblockMut = useMutation({
    mutationFn: () => api.tenants.unblock(tenant.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenant-detail", tenant.id] });
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setUnblockOpen(false);
    },
  });

  const isBlocked = tenant.statut === "suspendu";
  const isPaymentBlock = tenant.blockedReason === "payment_failed";
  const manualReason = tenant.blockedReason?.startsWith("manual:")
    ? tenant.blockedReason.slice("manual:".length)
    : null;

  const payments = paymentsQ.data ?? [];
  const last5 = payments.slice(0, 5);

  return (
    <div className="space-y-5">
      {/* ---- Accès du tenant (strip) ---- */}
      <Panel accent={isBlocked ? (isPaymentBlock ? "danger" : "warning") : undefined}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span
              className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                isBlocked ? "bg-red-500" : "bg-emerald-500"
              }`}
            />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-navy-400">
                Accès du tenant
              </p>
              <p className="text-base font-bold text-navy-800">
                {isBlocked
                  ? isPaymentBlock
                    ? "Bloqué · impayé Stripe"
                    : `Bloqué · ${manualReason ?? "blocage admin"}`
                  : "Actif"}
              </p>
              <p className="mt-0.5 text-xs text-navy-500">
                {isBlocked
                  ? "Sessions refusées en 403 (gérant, agents, portail client)."
                  : "Sessions acceptées · vérifié en quasi temps réel (cache 30 s)."}
                {tenant.blockedAt ? (
                  <> · bloqué le {fmtDateTime(tenant.blockedAt)}</>
                ) : null}
              </p>
            </div>
          </div>
          {isBlocked ? (
            <Button onClick={() => setUnblockOpen(true)}>Débloquer l'accès</Button>
          ) : (
            <Button variant="danger" onClick={() => setBlockOpen(true)}>
              Bloquer manuellement
            </Button>
          )}
        </div>
      </Panel>

      {/* ---- Barre de stats unifiée ---- */}
      <Panel padded={false}>
        <div className="grid grid-cols-2 divide-navy-100 sm:grid-cols-4 sm:divide-x">
          {[
            { l: "Utilisateurs", v: tenant.utilisateursCount },
            { l: "Sites", v: tenant.sitesCount },
            { l: "Agents", v: tenant.agentsCount },
            { l: "Factures émises", v: tenant.facturesEmisesCount },
          ].map((s) => (
            <div key={s.l} className="px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-navy-500">
                {s.l}
              </p>
              <p className="mt-1 text-2xl font-bold tracking-tight text-navy-800">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </Panel>

      {/* ---- Contenu principal + colonne latérale ---- */}
      <div className="grid gap-5 lg:grid-cols-3">
        {/* Derniers paiements */}
        <Panel padded={false} className="lg:col-span-2">
          <div className="flex items-center justify-between border-b border-navy-100 px-5 py-3.5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-navy-500">
              Derniers paiements
            </h3>
            <p className="text-xs text-navy-500">
              {payments.length} au total · détail dans « Facturation »
            </p>
          </div>
          {paymentsQ.isLoading ? (
            <p className="px-5 py-6 text-sm text-navy-500">Chargement…</p>
          ) : last5.length === 0 ? (
            <div className="p-5">
              <EmptyInline>Aucun paiement enregistré.</EmptyInline>
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-navy-100">
                {last5.map((p, i) => (
                  <PaymentRow key={`${p.source}-${p.ref}-${i}`} entry={p} />
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {/* Colonne latérale */}
        <div className="space-y-5">
          <Panel>
            <SectionTitle>Gérant initial</SectionTitle>
            {tenant.gerant ? (
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy-100 text-sm font-bold text-navy-700">
                  {(tenant.gerant.prenom[0] ?? "") + (tenant.gerant.nom[0] ?? "")}
                </span>
                <div className="min-w-0">
                  <p className="font-bold text-navy-800">
                    {tenant.gerant.prenom} {tenant.gerant.nom}
                  </p>
                  <a
                    href={`mailto:${tenant.gerant.email}`}
                    className="truncate text-sm text-accent-600 hover:underline"
                  >
                    {tenant.gerant.email}
                  </a>
                </div>
              </div>
            ) : (
              <p className="text-sm text-navy-500">Non trouvé.</p>
            )}
          </Panel>

          <Panel>
            <SectionTitle>Facturation client final</SectionTitle>
            <p className="text-3xl font-black tracking-tight text-navy-800">
              {fmtMoney(tenant.totalFactureHt)} €
            </p>
            <p className="mt-0.5 text-xs text-navy-500">Total HT cumulé depuis création</p>
            <div className="mt-3 border-t border-navy-100">
              <Field label="Dernière facture">
                {tenant.derniereFactureAt
                  ? new Date(tenant.derniereFactureAt).toLocaleDateString("fr-FR")
                  : "—"}
              </Field>
            </div>
          </Panel>
        </div>
      </div>

      {/* Modal blocage */}
      <Modal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        title="Bloquer manuellement le tenant"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            blockMut.mutate();
          }}
          className="space-y-4"
        >
          <p className="text-sm text-navy-600">
            Tous les utilisateurs (gérant, admins, agents PWA, portail client) seront
            immédiatement bloqués en 403. Cette action est tracée en audit.
          </p>
          <Input
            label="Motif (libre)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            required
            placeholder="Non-paiement persistant, abus, fraude…"
          />
          <div className="flex justify-end gap-2 border-t border-navy-100 pt-2">
            <Button type="button" variant="ghost" onClick={() => setBlockOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" variant="danger" disabled={blockMut.isPending || motif.length < 2}>
              {blockMut.isPending ? "Blocage…" : "Bloquer le tenant"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={unblockOpen}
        onClose={() => setUnblockOpen(false)}
        title="Débloquer le tenant ?"
        description={
          <p>
            L'accès sera rétabli immédiatement pour tous les utilisateurs. Si le tenant
            était bloqué pour impayé Stripe, l'abonnement repassera en « actif »
            indépendamment de l'état réel chez Stripe.
          </p>
        }
        confirmLabel="Débloquer"
        onConfirm={() => unblockMut.mutate()}
        busy={unblockMut.isPending}
      />
    </div>
  );
}

function PaymentRow({ entry: p }: { entry: SuperAdminPaymentEntry }) {
  return (
    <tr className="transition-colors hover:bg-navy-50/40">
      <td className="px-5 py-3 text-xs text-navy-500 whitespace-nowrap">
        {fmtDateTime(p.date)}
      </td>
      <td className="px-5 py-3">
        <p className="text-sm font-semibold text-navy-800">{p.libelle}</p>
        <p className="mt-0.5 flex items-center gap-2 text-[11px] text-navy-400">
          <span className="truncate">{p.ref}</span>
          {p.paymentMethodType ? (
            <span className="inline-flex items-center rounded-full bg-navy-100 px-2 py-0.5 font-bold uppercase tracking-wider text-navy-600">
              {labelPm(p.paymentMethodType)}
            </span>
          ) : null}
        </p>
      </td>
      <td className="px-5 py-3 text-right tabular-nums whitespace-nowrap">
        {p.montant != null ? (
          <span className="font-semibold text-navy-800">{fmtMoney(p.montant)} €</span>
        ) : p.quantite != null ? (
          <span className={`font-semibold ${p.quantite > 0 ? "text-emerald-700" : "text-navy-500"}`}>
            {p.quantite > 0 ? "+" : ""}
            {p.quantite}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-5 py-3">
        <Badge tone={STATUT_TONE[p.statut] ?? "slate"} dot>
          {p.statut}
        </Badge>
      </td>
    </tr>
  );
}

function labelPm(t: string): string {
  if (t === "sepa_debit") return "SEPA";
  if (t === "card") return "CB";
  if (t === "sofort") return "Sofort";
  if (t === "paypal") return "PayPal";
  return t;
}
